import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppLayout } from '../components/layout'
import { PageSkeleton, ErrorBoundary } from '../components/common'
import { AuthGuard, TierGuard, ContextGuard, ParentGuard } from './guards'

// Lazy load pages for code splitting
const Dashboard = lazy(() => import('../pages/dashboard').then(m => ({ default: m.DashboardPage })))
const Account = lazy(() => import('../pages/Account').then(m => ({ default: m.Account })))
const Explore = lazy(() => import('../pages/Explore').then(m => ({ default: m.Explore })))

// Auth pages (keep eager for fast initial load)
import { Login, Register, ForgotPassword, ResetPassword, VerifyEmail } from '../pages/auth'

// Public pages (lazy loaded)
const Landing = lazy(() => import('../pages/public').then(m => ({ default: m.Landing })))
const TermsOfService = lazy(() => import('../pages/public').then(m => ({ default: m.TermsOfService })))
const PrivacyPolicy = lazy(() => import('../pages/public').then(m => ({ default: m.PrivacyPolicy })))
const AccessibilityStatement = lazy(() => import('../pages/public').then(m => ({ default: m.AccessibilityStatement })))
const AcceptableUsePolicy = lazy(() => import('../pages/public').then(m => ({ default: m.AcceptableUsePolicy })))
const CommunityGuidelines = lazy(() => import('../pages/public').then(m => ({ default: m.CommunityGuidelines })))
const VendorTerms = lazy(() => import('../pages/public').then(m => ({ default: m.VendorTerms })))

// Auth pages (lazy loaded for new pages)
const AgeVerification = lazy(() => import('../pages/auth').then(m => ({ default: m.AgeVerification })))
const ParentalConsent = lazy(() => import('../pages/auth').then(m => ({ default: m.ParentalConsent })))
const RoleOnboarding = lazy(() => import('../pages/auth/RoleOnboarding').then(m => ({ default: m.RoleOnboarding })))

// Onboarding
const Onboarding = lazy(() => import('../pages/auth/Onboarding').then(m => ({ default: m.Onboarding })))

// Demo
const DemoLanding = lazy(() => import('../pages/auth/DemoLanding').then(m => ({ default: m.DemoLanding })))

// Figma plugin showcase (dev only)
const FigmaShowcase = lazy(() => import('../pages/FigmaShowcase'))

// Challenge pages
const MyChallenges = lazy(() => import('../pages/challenges').then(m => ({ default: m.MyChallenges })))
const ChallengeDetail = lazy(() => import('../pages/challenges').then(m => ({ default: m.ChallengeDetail })))
const TrackRecord = lazy(() => import('../pages/challenges').then(m => ({ default: m.TrackRecord })))
const GiftRedemption = lazy(() => import('../pages/challenges').then(m => ({ default: m.GiftRedemption })))
const GroupChallenge = lazy(() => import('../pages/challenges').then(m => ({ default: m.GroupChallenge })))
const SubmitChallenge = lazy(() => import('../pages/challenges').then(m => ({ default: m.SubmitChallenge })))

// Checkout pages
const CheckoutPage = lazy(() => import('../pages/checkout').then(m => ({ default: m.CheckoutPage })))
const OrderHistory = lazy(() => import('../pages/checkout').then(m => ({ default: m.OrderHistory })))

// Explore pages
const SavedList = lazy(() => import('../pages/explore/SavedList').then(m => ({ default: m.SavedList })))
const ExploreCategoryView = lazy(() => import('../pages/explore/ExploreCategoryView').then(m => ({ default: m.ExploreCategoryView })))
const DealersChoicePage = lazy(() => import('../pages/explore/DealersChoicePage').then(m => ({ default: m.DealersChoicePage })))
const MapView = lazy(() => import('../pages/explore/MapView').then(m => ({ default: m.MapView })))

// Provider pages
const ChallengeProviderProfile = lazy(() => import('../pages/providers').then(m => ({ default: m.ChallengeProviderProfile })))

// Social pages
const Communities = lazy(() => import('../pages/communities/Communities').then(m => ({ default: m.Communities })))
const CommunityDetail = lazy(() => import('../pages/communities/CommunityDetail').then(m => ({ default: m.CommunityDetail })))
const SchoolCommunity = lazy(() => import('../pages/communities/SchoolCommunity').then(m => ({ default: m.SchoolCommunity })))
const PrivateCommunities = lazy(() => import('../pages/communities/PrivateCommunities').then(m => ({ default: m.PrivateCommunities })))
const CreateCommunity = lazy(() => import('../pages/communities/CreateCommunity').then(m => ({ default: m.CreateCommunity })))
const AssignmentProgress = lazy(() => import('../pages/communities/AssignmentProgress').then(m => ({ default: m.AssignmentProgress })))
const CommunityGoals = lazy(() => import('../pages/communities/CommunityGoals').then(m => ({ default: m.CommunityGoals })))
const Gifts = lazy(() => import('../pages/gifts/Gifts').then(m => ({ default: m.Gifts })))
const Invitations = lazy(() => import('../pages/Invitations').then(m => ({ default: m.Invitations })))
const PartnerFinder = lazy(() => import('../pages/social').then(m => ({ default: m.PartnerFinder })))

// School pages
const SchoolDashboard = lazy(() => import('../pages/school').then(m => ({ default: m.SchoolDashboard })))
const Roster = lazy(() => import('../pages/school').then(m => ({ default: m.Roster })))
const Classes = lazy(() => import('../pages/school').then(m => ({ default: m.Classes })))
const Grades = lazy(() => import('../pages/school').then(m => ({ default: m.Grades })))
const Teachers = lazy(() => import('../pages/school').then(m => ({ default: m.Teachers })))
const SchoolAssignments = lazy(() => import('../pages/school').then(m => ({ default: m.Assignments })))
const ClassProgress = lazy(() => import('../pages/school').then(m => ({ default: m.ClassProgress })))
const TeacherReviews = lazy(() => import('../pages/school').then(m => ({ default: m.TeacherReviews })))
const PurchaseRequests = lazy(() => import('../pages/school').then(m => ({ default: m.PurchaseRequests })))
const Surveys = lazy(() => import('../pages/school').then(m => ({ default: m.Surveys })))
const ReflectionAnalytics = lazy(() => import('../pages/school/ReflectionAnalytics').then(m => ({ default: m.ReflectionAnalytics })))
const PurchaseRequestQueue = lazy(() => import('../pages/school').then(m => ({ default: m.PurchaseRequestQueue })))
const BulkGiftChallenges = lazy(() => import('../pages/school').then(m => ({ default: m.BulkGiftChallenges })))
const AlumniTransition = lazy(() => import('../pages/school').then(m => ({ default: m.AlumniTransition })))

// Admin pages
const AdminHome = lazy(() => import('../pages/admin').then(m => ({ default: m.AdminHome })))
const UserManagement = lazy(() => import('../pages/admin').then(m => ({ default: m.UserManagement })))
const VerificationQueue = lazy(() => import('../pages/admin').then(m => ({ default: m.VerificationQueue })))
const VendorManagement = lazy(() => import('../pages/admin').then(m => ({ default: m.VendorManagement })))
const CouponManagement = lazy(() => import('../pages/admin').then(m => ({ default: m.CouponManagement })))
const ChallengeManagement = lazy(() => import('../pages/admin').then(m => ({ default: m.ChallengeManagement })))
const ChallengeSeriesManagement = lazy(() => import('../pages/admin').then(m => ({ default: m.ChallengeSeriesManagement })))
const ReviewModeration = lazy(() => import('../pages/admin').then(m => ({ default: m.ReviewModeration })))
const OrderManagement = lazy(() => import('../pages/admin').then(m => ({ default: m.OrderManagement })))
const SchoolsManagement = lazy(() => import('../pages/admin').then(m => ({ default: m.SchoolsManagement })))
const PlatformSettings = lazy(() => import('../pages/admin').then(m => ({ default: m.PlatformSettings })))
const AnalyticsDashboard = lazy(() => import('../pages/admin').then(m => ({ default: m.AnalyticsDashboard })))
const UserDetail = lazy(() => import('../pages/admin/UserDetail').then(m => ({ default: m.UserDetail })))
const AuditLog = lazy(() => import('../pages/admin/AuditLog').then(m => ({ default: m.AuditLog })))
const FlaggedContentQueue = lazy(() => import('../pages/admin/FlaggedContentQueue').then(m => ({ default: m.FlaggedContentQueue })))
const AdminRoles = lazy(() => import('../pages/admin/AdminRoles').then(m => ({ default: m.AdminRoles })))
const ReflectionAnalyticsAdmin = lazy(() => import('../pages/admin/ReflectionAnalyticsAdmin').then(m => ({ default: m.ReflectionAnalyticsAdmin })))
const ChallengeApprovalQueue = lazy(() => import('../pages/admin').then(m => ({ default: m.ChallengeApprovalQueue })))
const FeaturedContent = lazy(() => import('../pages/admin').then(m => ({ default: m.FeaturedContent })))
const SchoolHealthDashboard = lazy(() => import('../pages/admin').then(m => ({ default: m.SchoolHealthDashboard })))
const ProductAnalytics = lazy(() => import('../pages/admin').then(m => ({ default: m.ProductAnalytics })))
const ContentAnalytics = lazy(() => import('../pages/admin').then(m => ({ default: m.ContentAnalytics })))
const EngineeringAnalytics = lazy(() => import('../pages/admin').then(m => ({ default: m.EngineeringAnalytics })))
const ReportBuilder = lazy(() => import('../pages/admin').then(m => ({ default: m.ReportBuilder })))
const DesignTokenEditor = lazy(() => import('../pages/admin/DesignTokenEditor').then(m => ({ default: m.DesignTokenEditor })))
const ComponentShowcase = lazy(() => import('../pages/admin/ComponentShowcase').then(m => ({ default: m.ComponentShowcase })))
const FeatureFlagManagement = lazy(() => import('../pages/admin').then(m => ({ default: m.FeatureFlagManagement })))
const ExecutiveDashboard = lazy(() => import('../pages/admin').then(m => ({ default: m.ExecutiveDashboard })))
const FeatureUsageAnalytics = lazy(() => import('../pages/admin').then(m => ({ default: m.FeatureUsageAnalytics })))
const ABTestBuilder = lazy(() => import('../pages/admin').then(m => ({ default: m.ABTestBuilder })))
const FunnelBuilder = lazy(() => import('../pages/admin').then(m => ({ default: m.FunnelBuilder })))
const CampaignAttribution = lazy(() => import('../pages/admin').then(m => ({ default: m.CampaignAttribution })))
const ProductTracker = lazy(() => import('../pages/admin/ProductTracker').then(m => ({ default: m.ProductTracker })))
const TrackerItemDetail = lazy(() => import('../pages/admin/TrackerItemDetail').then(m => ({ default: m.TrackerItemDetail })))
const DocFeedbackManager = lazy(() => import('../pages/admin/DocFeedbackManager').then(m => ({ default: m.DocFeedbackManager })))
const ClassroomDashboard = lazy(() => import('../pages/school').then(m => ({ default: m.ClassroomDashboard })))
const LearningPathDesigner = lazy(() => import('../pages/school').then(m => ({ default: m.LearningPathDesigner })))
const SessionReplay = lazy(() => import('../pages/admin').then(m => ({ default: m.SessionReplay })))
const KnowledgeBaseBuilder = lazy(() => import('../pages/admin').then(m => ({ default: m.KnowledgeBaseBuilder })))
const WhiteLabelPreview = lazy(() => import('../pages/admin').then(m => ({ default: m.WhiteLabelPreview })))
const MultiSchoolDashboard = lazy(() => import('../pages/admin').then(m => ({ default: m.MultiSchoolDashboard })))
const ComplianceMonitor = lazy(() => import('../pages/admin').then(m => ({ default: m.ComplianceMonitor })))
const IntegrationsHub = lazy(() => import('../pages/admin').then(m => ({ default: m.IntegrationsHub })))
const DataExportDeletion = lazy(() => import('../pages/admin').then(m => ({ default: m.DataExportDeletion })))
const NorthStarTracker = lazy(() => import('../pages/admin').then(m => ({ default: m.NorthStarTracker })))
const CohortAnalysis = lazy(() => import('../pages/admin').then(m => ({ default: m.CohortAnalysis })))
const UserHealthScore = lazy(() => import('../pages/admin').then(m => ({ default: m.UserHealthScore })))
const HeatmapClickTracker = lazy(() => import('../pages/admin').then(m => ({ default: m.HeatmapClickTracker })))
const UserJourneyMaps = lazy(() => import('../pages/admin').then(m => ({ default: m.UserJourneyMaps })))
const LeadScoringDashboard = lazy(() => import('../pages/admin').then(m => ({ default: m.LeadScoringDashboard })))
const SEODashboard = lazy(() => import('../pages/admin').then(m => ({ default: m.SEODashboard })))
const LandingPageBuilder = lazy(() => import('../pages/admin').then(m => ({ default: m.LandingPageBuilder })))
const EmailTemplateBuilder = lazy(() => import('../pages/admin').then(m => ({ default: m.EmailTemplateBuilder })))
const SocialProofGenerator = lazy(() => import('../pages/admin').then(m => ({ default: m.SocialProofGenerator })))
const ReferralProgramManager = lazy(() => import('../pages/admin').then(m => ({ default: m.ReferralProgramManager })))
const TrialManager = lazy(() => import('../pages/admin').then(m => ({ default: m.TrialManager })))
const CompetitiveIntel = lazy(() => import('../pages/admin').then(m => ({ default: m.CompetitiveIntel })))
const InAppSurveys = lazy(() => import('../pages/admin').then(m => ({ default: m.InAppSurveys })))
const InvestorDataRoom = lazy(() => import('../pages/admin').then(m => ({ default: m.InvestorDataRoom })))
const SalesDeckGenerator = lazy(() => import('../pages/admin').then(m => ({ default: m.SalesDeckGenerator })))
const PricingConfigurator = lazy(() => import('../pages/admin').then(m => ({ default: m.PricingConfigurator })))
const ROICalculator = lazy(() => import('../pages/admin').then(m => ({ default: m.ROICalculator })))
const BudgetTracker = lazy(() => import('../pages/admin').then(m => ({ default: m.BudgetTracker })))
const CostTracker = lazy(() => import('../pages/admin').then(m => ({ default: m.CostTracker })))
const OKRTracker = lazy(() => import('../pages/admin').then(m => ({ default: m.OKRTracker })))
const ContentCalendar = lazy(() => import('../pages/admin').then(m => ({ default: m.ContentCalendar })))
const ReleaseNotesManager = lazy(() => import('../pages/admin').then(m => ({ default: m.ReleaseNotesManager })))
const InternalWiki = lazy(() => import('../pages/admin').then(m => ({ default: m.InternalWiki })))
const ExportAnything = lazy(() => import('../pages/admin').then(m => ({ default: m.ExportAnything })))
const TeacherEffectivenessView = lazy(() => import('../pages/admin').then(m => ({ default: m.TeacherEffectivenessView })))
const OnboardingWizardBuilder = lazy(() => import('../pages/admin').then(m => ({ default: m.OnboardingWizardBuilder })))
const DeploymentDashboard = lazy(() => import('../pages/admin').then(m => ({ default: m.DeploymentDashboard })))
const APIPlayground = lazy(() => import('../pages/admin').then(m => ({ default: m.APIPlayground })))
const PerformanceProfiler = lazy(() => import('../pages/admin').then(m => ({ default: m.PerformanceProfiler })))
const ErrorDashboard = lazy(() => import('../pages/admin').then(m => ({ default: m.ErrorDashboard })))
const UptimeMonitor = lazy(() => import('../pages/admin').then(m => ({ default: m.UptimeMonitor })))
const EnvironmentSwitcher = lazy(() => import('../pages/admin').then(m => ({ default: m.EnvironmentSwitcher })))
const SchemaDiffViewer = lazy(() => import('../pages/admin').then(m => ({ default: m.SchemaDiffViewer })))
const FeatureFlagRolloutPlanner = lazy(() => import('../pages/admin').then(m => ({ default: m.FeatureFlagRolloutPlanner })))
const CoverageMap = lazy(() => import('../pages/admin').then(m => ({ default: m.CoverageMap })))
const TestScenarioManager = lazy(() => import('../pages/admin').then(m => ({ default: m.TestScenarioManager })))
const MockDataGenerator = lazy(() => import('../pages/admin').then(m => ({ default: m.MockDataGenerator })))
const ChaosMode = lazy(() => import('../pages/admin').then(m => ({ default: m.ChaosMode })))
const VisualRegressionDashboard = lazy(() => import('../pages/admin').then(m => ({ default: m.VisualRegressionDashboard })))
const BugReportWidget = lazy(() => import('../pages/admin').then(m => ({ default: m.BugReportWidget })))
const AccessibilityAuditPanel = lazy(() => import('../pages/admin').then(m => ({ default: m.AccessibilityAuditPanel })))
const DesignTokenChangelog = lazy(() => import('../pages/admin').then(m => ({ default: m.DesignTokenChangelog })))
const ResponsivePreview = lazy(() => import('../pages/admin').then(m => ({ default: m.ResponsivePreview })))
const UserFlowRecorder = lazy(() => import('../pages/admin').then(m => ({ default: m.UserFlowRecorder })))
const AnnotationMode = lazy(() => import('../pages/admin').then(m => ({ default: m.AnnotationMode })))
const OnboardingTourBuilder = lazy(() => import('../pages/admin').then(m => ({ default: m.OnboardingTourBuilder })))
const FeatureRequestBoard = lazy(() => import('../pages/admin').then(m => ({ default: m.FeatureRequestBoard })))
const ConsentDashboard = lazy(() => import('../pages/admin').then(m => ({ default: m.ConsentDashboard })))
const TermsVersionManager = lazy(() => import('../pages/admin').then(m => ({ default: m.TermsVersionManager })))
const PIIScanner = lazy(() => import('../pages/admin').then(m => ({ default: m.PIIScanner })))
const SupportQuickActions = lazy(() => import('../pages/admin').then(m => ({ default: m.SupportQuickActions })))
const SupportHistory = lazy(() => import('../pages/admin').then(m => ({ default: m.SupportHistory })))

// Account sub-pages
const AccountDeletion = lazy(() => import('../pages/account/AccountDeletion').then(m => ({ default: m.AccountDeletion })))
const DataExportRequest = lazy(() => import('../pages/account/DataExportRequest').then(m => ({ default: m.DataExportRequest })))
const AppealForm = lazy(() => import('../pages/account/AppealForm').then(m => ({ default: m.AppealForm })))
const LinkedAccounts = lazy(() => import('../pages/account/LinkedAccounts').then(m => ({ default: m.LinkedAccounts })))
const DataAccessRequest = lazy(() => import('../pages/account/DataAccessRequest').then(m => ({ default: m.DataAccessRequest })))

// Notification pages
const Notifications = lazy(() => import('../pages/Notifications').then(m => ({ default: m.Notifications })))

// Parent pages
const ParentDashboard = lazy(() => import('../pages/parent').then(m => ({ default: m.ParentDashboard })))

// Vendor pages
const VendorDashboard = lazy(() => import('../pages/vendor').then(m => ({ default: m.VendorDashboard })))
const VendorApplication = lazy(() => import('../pages/vendor/VendorApplication').then(m => ({ default: m.VendorApplication })))
const VendorChallengeCreate = lazy(() => import('../pages/vendor/VendorChallengeCreate').then(m => ({ default: m.VendorChallengeCreate })))
const VendorEvents = lazy(() => import('../pages/vendor/VendorEvents').then(m => ({ default: m.VendorEvents })))
const VendorAnalyticsPage = lazy(() => import('../pages/vendor/VendorAnalytics').then(m => ({ default: m.VendorAnalytics })))
const VendorChallenges = lazy(() => import('../pages/vendor/VendorChallenges').then(m => ({ default: m.VendorChallenges })))
const VendorOrders = lazy(() => import('../pages/vendor/VendorOrders').then(m => ({ default: m.VendorOrders })))
const VendorSettings = lazy(() => import('../pages/vendor/VendorSettings').then(m => ({ default: m.VendorSettings })))

// Learning Path pages
const LearningPaths = lazy(() => import('../pages/learningPaths').then(m => ({ default: m.LearningPaths })))
const LearningPathDetail = lazy(() => import('../pages/learningPaths').then(m => ({ default: m.LearningPathDetail })))

// Portfolio pages
const Portfolios = lazy(() => import('../pages/portfolio').then(m => ({ default: m.Portfolios })))
const PortfolioView = lazy(() => import('../pages/portfolio').then(m => ({ default: m.PortfolioView })))

// Profile / Progress pages
const JourneyMap = lazy(() => import('../pages/profile').then(m => ({ default: m.JourneyMap })))
const Badges = lazy(() => import('../pages/profile').then(m => ({ default: m.Badges })))
const XPHistory = lazy(() => import('../pages/profile').then(m => ({ default: m.XPHistory })))
const PublicProfile = lazy(() => import('../pages/profile').then(m => ({ default: m.PublicProfile })))
const MyReflections = lazy(() => import('../pages/profile/MyReflections').then(m => ({ default: m.MyReflections })))

/**
 * Suspense wrapper with loading fallback
 */
function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

/**
 * Application Router
 *
 * Routes are organized into:
 * - Public routes: Accessible without authentication (login, register, etc.)
 * - Protected routes: Require authentication (dashboard, account, etc.)
 *
 * Uses React.lazy() for code splitting - pages are loaded on demand.
 */
export const router = createBrowserRouter([
  // ============================================================================
  // Public Routes (no auth required)
  // ============================================================================
  {
    path: '/demo',
    element: <LazyPage><DemoLanding /></LazyPage>,
  },
  {
    path: '/figma-showcase',
    element: <LazyPage><FigmaShowcase /></LazyPage>,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/register',
    element: <Register />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/reset-password/:token',
    element: <ResetPassword />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail />,
  },
  // Public content pages
  {
    path: '/welcome',
    element: <LazyPage><Landing /></LazyPage>,
  },
  {
    path: '/terms',
    element: <LazyPage><TermsOfService /></LazyPage>,
  },
  {
    path: '/privacy',
    element: <LazyPage><PrivacyPolicy /></LazyPage>,
  },
  {
    path: '/accessibility',
    element: <LazyPage><AccessibilityStatement /></LazyPage>,
  },
  {
    path: '/onboarding',
    element: <LazyPage><Onboarding /></LazyPage>,
  },
  {
    path: '/age-verification',
    element: <LazyPage><AgeVerification /></LazyPage>,
  },
  {
    path: '/consent/:token',
    element: <LazyPage><ParentalConsent /></LazyPage>,
  },
  {
    path: '/acceptable-use',
    element: <LazyPage><AcceptableUsePolicy /></LazyPage>,
  },
  {
    path: '/community-guidelines',
    element: <LazyPage><CommunityGuidelines /></LazyPage>,
  },
  {
    path: '/vendor-terms',
    element: <LazyPage><VendorTerms /></LazyPage>,
  },
  {
    path: '/onboarding/:role',
    element: <LazyPage><RoleOnboarding /></LazyPage>,
  },
  {
    path: '/vendor/apply',
    element: <LazyPage><VendorApplication /></LazyPage>,
  },

  // ============================================================================
  // Protected Routes (auth required)
  // ============================================================================
  {
    path: '/',
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    children: [
      // Dashboard (home)
      {
        index: true,
        element: <LazyPage><Dashboard /></LazyPage>,
      },

      // Account
      {
        path: 'account',
        element: <LazyPage><Account /></LazyPage>,
      },

      // Account Deletion
      {
        path: 'account/delete',
        element: <LazyPage><AccountDeletion /></LazyPage>,
      },

      // Data Export Request
      {
        path: 'account/data-export',
        element: <LazyPage><DataExportRequest /></LazyPage>,
      },

      // Appeal Moderation Decision
      {
        path: 'account/appeal',
        element: <LazyPage><AppealForm /></LazyPage>,
      },

      // Linked Accounts
      {
        path: 'account/linked-accounts',
        element: <LazyPage><LinkedAccounts /></LazyPage>,
      },

      // Data Subject Access Request
      {
        path: 'account/data-access-request',
        element: <LazyPage><DataAccessRequest /></LazyPage>,
      },

      // Notifications
      {
        path: 'notifications',
        element: <LazyPage><Notifications /></LazyPage>,
      },

      // Explore
      {
        path: 'explore',
        element: <LazyPage><Explore /></LazyPage>,
      },

      // Explore search (uses same Explore page with query params)
      {
        path: 'explore/search',
        element: <LazyPage><Explore /></LazyPage>,
      },

      // Explore category views
      {
        path: 'explore/best-fit',
        element: <LazyPage><ExploreCategoryView type="best-fit" /></LazyPage>,
      },
      {
        path: 'explore/featured',
        element: <LazyPage><ExploreCategoryView type="featured" /></LazyPage>,
      },
      {
        path: 'explore/free',
        element: <LazyPage><ExploreCategoryView type="free" /></LazyPage>,
      },
      {
        path: 'explore/popular',
        element: <LazyPage><ExploreCategoryView type="popular" /></LazyPage>,
      },
      {
        path: 'explore/new',
        element: <LazyPage><ExploreCategoryView type="new" /></LazyPage>,
      },
      {
        path: 'explore/category/:slug',
        element: <LazyPage><ExploreCategoryView type="category" /></LazyPage>,
      },

      // Dealer's Choice
      {
        path: 'dealers-choice',
        element: <LazyPage><DealersChoicePage /></LazyPage>,
      },

      // Map View
      {
        path: 'explore/map',
        element: <LazyPage><MapView /></LazyPage>,
      },

      // Challenge Provider Profile
      {
        path: 'providers/:vendorId',
        element: <LazyPage><ChallengeProviderProfile /></LazyPage>,
      },

      // ========================================================================
      // Challenge routes
      // ========================================================================

      // Challenge Detail
      {
        path: 'challenges/:id',
        element: <LazyPage><ChallengeDetail /></LazyPage>,
      },

      // My Challenges
      {
        path: 'my-challenges',
        element: <LazyPage><MyChallenges /></LazyPage>,
      },

      // Track Record
      {
        path: 'my-challenges/:id/track',
        element: <LazyPage><TrackRecord /></LazyPage>,
      },

      // Gift Redemption
      {
        path: 'challenges/gift/:code',
        element: <LazyPage><GiftRedemption /></LazyPage>,
      },

      // Group Challenge
      {
        path: 'challenges/group/:id',
        element: <LazyPage><GroupChallenge /></LazyPage>,
      },

      // Submit Challenge (user-created challenge submission)
      {
        path: 'challenges/submit',
        element: <LazyPage><SubmitChallenge /></LazyPage>,
      },

      // Checkout
      {
        path: 'checkout',
        element: <LazyPage><CheckoutPage /></LazyPage>,
      },

      // Order History
      {
        path: 'orders',
        element: <LazyPage><OrderHistory /></LazyPage>,
      },

      // ========================================================================
      // Explore features
      // ========================================================================

      // Saved List (consolidated from bucket list and interesting list)
      {
        path: 'saved',
        element: <TierGuard><LazyPage><SavedList /></LazyPage></TierGuard>,
      },

      // Redirects from old routes
      {
        path: 'bucket-list',
        element: <Navigate to="/saved" replace />,
      },
      {
        path: 'interesting',
        element: <Navigate to="/saved" replace />,
      },

      // Communities
      {
        path: 'communities',
        element: <LazyPage><Communities /></LazyPage>,
      },

      // School Communities
      {
        path: 'communities/school',
        element: <LazyPage><SchoolCommunity level="full_school" /></LazyPage>,
      },
      {
        path: 'communities/grade',
        element: <LazyPage><SchoolCommunity level="grade" /></LazyPage>,
      },
      {
        path: 'communities/class',
        element: <LazyPage><SchoolCommunity level="class" /></LazyPage>,
      },

      // Private Communities (not available for Tier 1 school-only accounts)
      {
        path: 'communities/private',
        element: <TierGuard><LazyPage><PrivateCommunities /></LazyPage></TierGuard>,
      },
      // Create Community (must come before :id catch-all)
      {
        path: 'communities/create',
        element: <LazyPage><CreateCommunity /></LazyPage>,
      },

      // Community Assignment Progress (for teachers to view student progress)
      {
        path: 'communities/:communityId/assignments/:assignmentId',
        element: <LazyPage><AssignmentProgress /></LazyPage>,
      },

      // Community Goals
      {
        path: 'communities/:communityId/goals',
        element: <LazyPage><CommunityGoals /></LazyPage>,
      },
      {
        path: 'communities/:communityId/goals/:goalId',
        element: <LazyPage><CommunityGoals /></LazyPage>,
      },

      // Community Detail (keep at end to avoid matching before specific routes)
      {
        path: 'communities/:id',
        element: <LazyPage><CommunityDetail /></LazyPage>,
      },

      // Gifts
      {
        path: 'gifts',
        element: <TierGuard><LazyPage><Gifts /></LazyPage></TierGuard>,
      },
      {
        path: 'invitations',
        element: <LazyPage><Invitations /></LazyPage>,
      },
      {
        path: 'partner-finder',
        element: <LazyPage><PartnerFinder /></LazyPage>,
      },

      // ========================================================================
      // Learning Paths
      // ========================================================================
      {
        path: 'learning-paths',
        element: <LazyPage><LearningPaths /></LazyPage>,
      },
      {
        path: 'learning-paths/:id',
        element: <LazyPage><LearningPathDetail /></LazyPage>,
      },

      // ========================================================================
      // Portfolio
      // ========================================================================
      {
        path: 'portfolios',
        element: <LazyPage><Portfolios /></LazyPage>,
      },
      {
        path: 'portfolios/:id',
        element: <LazyPage><PortfolioView /></LazyPage>,
      },

      // ========================================================================
      // Progress & Gamification
      // ========================================================================
      {
        path: 'journey-map',
        element: <LazyPage><JourneyMap /></LazyPage>,
      },
      {
        path: 'badges',
        element: <LazyPage><Badges /></LazyPage>,
      },
      {
        path: 'xp-history',
        element: <LazyPage><XPHistory /></LazyPage>,
      },

      // Public User Profile
      {
        path: 'users/:userId',
        element: <LazyPage><PublicProfile /></LazyPage>,
      },

      // My Reflections
      {
        path: 'reflections',
        element: <LazyPage><MyReflections /></LazyPage>,
      },

      // ========================================================================
      // Parent Dashboard
      // ========================================================================
      {
        path: 'parent',
        element: <ParentGuard><LazyPage><ParentDashboard /></LazyPage></ParentGuard>,
      },

      // ========================================================================
      // Vendor Dashboard
      // ========================================================================
      {
        path: 'vendor',
        element: <ContextGuard context="business"><LazyPage><VendorDashboard /></LazyPage></ContextGuard>,
      },

      // ========================================================================
      // School Administration routes
      // ========================================================================

      // School Dashboard
      {
        path: 'school',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin', 'teacher']}><LazyPage><SchoolDashboard /></LazyPage></ContextGuard>,
      },

      // Classroom Dashboard (teachers + admins)
      {
        path: 'school/classroom',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin', 'teacher']}><LazyPage><ClassroomDashboard /></LazyPage></ContextGuard>,
      },

      // Learning Path Designer (teachers + admins)
      {
        path: 'school/learning-path-designer',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin', 'teacher']}><LazyPage><LearningPathDesigner /></LazyPage></ContextGuard>,
      },

      // Roster Management (admin only)
      {
        path: 'school/roster',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin']}><LazyPage><Roster /></LazyPage></ContextGuard>,
      },

      // Classes Management (admin only)
      {
        path: 'school/classes',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin']}><LazyPage><Classes /></LazyPage></ContextGuard>,
      },

      // Grades Management (admin + teacher)
      {
        path: 'school/grades',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin', 'teacher']}><LazyPage><Grades /></LazyPage></ContextGuard>,
      },

      // Teachers Management (admin only)
      {
        path: 'school/teachers',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin']}><LazyPage><Teachers /></LazyPage></ContextGuard>,
      },

      // Assignments (admin + teacher)
      {
        path: 'school/assignments',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin', 'teacher']}><LazyPage><SchoolAssignments /></LazyPage></ContextGuard>,
      },

      // Class Progress (admin + teacher)
      {
        path: 'school/classes/:classId/progress',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin', 'teacher']}><LazyPage><ClassProgress /></LazyPage></ContextGuard>,
      },

      // Teacher Reviews (admin + teacher)
      {
        path: 'school/reviews',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin', 'teacher']}><LazyPage><TeacherReviews /></LazyPage></ContextGuard>,
      },

      // Surveys (admin only)

      {
        path: 'school/surveys',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin']}><LazyPage><Surveys /></LazyPage></ContextGuard>,
      },

      // School Reflection Analytics (admin + teacher)
      {
        path: 'school/reflection-analytics',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin', 'teacher']}><LazyPage><ReflectionAnalytics /></LazyPage></ContextGuard>,
      },

      // Purchase Request Queue (admin only)
      {
        path: 'school/purchase-requests',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin']}><LazyPage><PurchaseRequestQueue /></LazyPage></ContextGuard>,
      },

      // Bulk Gift Challenges (admin only)
      {
        path: 'school/bulk-gift',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin']}><LazyPage><BulkGiftChallenges /></LazyPage></ContextGuard>,
      },

      // Alumni Transition (admin only)
      {
        path: 'school/alumni-transition',
        element: <ContextGuard context="school" roles={['head_school_admin', 'school_admin']}><LazyPage><AlumniTransition /></LazyPage></ContextGuard>,
      },

      // Student-specific school routes
      {
        path: 'school/my-assignments',
        element: <ContextGuard context="school" roles={['student']}><LazyPage><SchoolAssignments /></LazyPage></ContextGuard>,
      },
      {
        path: 'school/my-grades',
        element: <ContextGuard context="school" roles={['student']}><LazyPage><Grades /></LazyPage></ContextGuard>,
      },

      // Parent school route
      {
        path: 'school/child-grades',
        element: <ParentGuard><LazyPage><Grades /></LazyPage></ParentGuard>,
      },

      // Vendor sub-routes
      {
        path: 'vendor/challenges',
        element: <ContextGuard context="business"><LazyPage><VendorChallenges /></LazyPage></ContextGuard>,
      },
      {
        path: 'vendor/challenges/create',
        element: <ContextGuard context="business"><LazyPage><VendorChallengeCreate /></LazyPage></ContextGuard>,
      },
      {
        path: 'vendor/orders',
        element: <ContextGuard context="business"><LazyPage><VendorOrders /></LazyPage></ContextGuard>,
      },
      {
        path: 'vendor/analytics',
        element: <ContextGuard context="business"><LazyPage><VendorAnalyticsPage /></LazyPage></ContextGuard>,
      },
      {
        path: 'vendor/events',
        element: <ContextGuard context="business"><LazyPage><VendorEvents /></LazyPage></ContextGuard>,
      },
      {
        path: 'vendor/settings',
        element: <ContextGuard context="business"><LazyPage><VendorSettings /></LazyPage></ContextGuard>,
      },

      // Admin section (platform_admin + staff)
      {
        path: 'admin',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><AdminHome /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/vendors',
        element: <ContextGuard context="platform"><LazyPage><VendorManagement /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/users',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><UserManagement /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/coupons',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><CouponManagement /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/challenges',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ChallengeManagement /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/challenge-series',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ChallengeSeriesManagement /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/reviews',
        element: <ContextGuard context="platform"><LazyPage><ReviewModeration /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/orders',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><OrderManagement /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/verification',
        element: <ContextGuard context="platform"><LazyPage><VerificationQueue /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/schools',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><SchoolsManagement /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/settings',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><PlatformSettings /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/executive-dashboard',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ExecutiveDashboard /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/feature-usage',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><FeatureUsageAnalytics /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/ab-tests',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ABTestBuilder /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/funnels',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><FunnelBuilder /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/campaigns',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><CampaignAttribution /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/session-replay',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><SessionReplay /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/knowledge-base',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><KnowledgeBaseBuilder /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/white-label',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><WhiteLabelPreview /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/multi-school',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><MultiSchoolDashboard /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/compliance',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ComplianceMonitor /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/integrations',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><IntegrationsHub /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/data-requests',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><DataExportDeletion /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/analytics',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><AnalyticsDashboard /></LazyPage></ContextGuard>,
      },

      // Admin detail pages (Phase 8)
      {
        path: 'admin/users/:userId',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><UserDetail /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/audit-log',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><AuditLog /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/tracker',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ProductTracker /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/tracker/:id',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><TrackerItemDetail /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/doc-feedback',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><DocFeedbackManager /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/flagged-content',
        element: <ContextGuard context="platform"><LazyPage><FlaggedContentQueue /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/roles',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><AdminRoles /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/reflection-analytics',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ReflectionAnalyticsAdmin /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/challenge-queue',
        element: <ContextGuard context="platform"><LazyPage><ChallengeApprovalQueue /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/featured-content',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><FeaturedContent /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/school-health',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><SchoolHealthDashboard /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/product-analytics',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ProductAnalytics /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/content-analytics',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ContentAnalytics /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/engineering-analytics',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><EngineeringAnalytics /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/report-builder',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ReportBuilder /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/theme-editor',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><DesignTokenEditor /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/component-showcase',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ComponentShowcase /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/feature-flags',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><FeatureFlagManagement /></LazyPage></ContextGuard>,
      },

      // Analytics tools
      {
        path: 'admin/north-star',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><NorthStarTracker /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/cohort-analysis',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><CohortAnalysis /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/user-health',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><UserHealthScore /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/heatmaps',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><HeatmapClickTracker /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/user-journeys',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><UserJourneyMaps /></LazyPage></ContextGuard>,
      },

      // Growth & Marketing tools
      {
        path: 'admin/lead-scoring',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><LeadScoringDashboard /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/seo',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><SEODashboard /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/landing-pages',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><LandingPageBuilder /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/email-templates',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><EmailTemplateBuilder /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/social-proof',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><SocialProofGenerator /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/referral-program',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ReferralProgramManager /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/trial-manager',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><TrialManager /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/competitive-intel',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><CompetitiveIntel /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/surveys',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><InAppSurveys /></LazyPage></ContextGuard>,
      },

      // Sales & Finance tools
      {
        path: 'admin/investor-data-room',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><InvestorDataRoom /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/sales-decks',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><SalesDeckGenerator /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/pricing',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><PricingConfigurator /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/roi-calculator',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ROICalculator /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/budget',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><BudgetTracker /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/costs',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><CostTracker /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/okrs',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><OKRTracker /></LazyPage></ContextGuard>,
      },

      // Content tools
      {
        path: 'admin/content-calendar',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ContentCalendar /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/release-notes',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ReleaseNotesManager /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/wiki',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><InternalWiki /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/exports',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ExportAnything /></LazyPage></ContextGuard>,
      },

      // Schools tools
      {
        path: 'admin/teacher-effectiveness',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><TeacherEffectivenessView /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/onboarding-wizards',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><OnboardingWizardBuilder /></LazyPage></ContextGuard>,
      },

      // Engineering tools
      {
        path: 'admin/deployments',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><DeploymentDashboard /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/api-playground',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><APIPlayground /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/performance',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><PerformanceProfiler /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/error-dashboard',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ErrorDashboard /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/uptime',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><UptimeMonitor /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/environments',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><EnvironmentSwitcher /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/schema-diff',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><SchemaDiffViewer /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/flag-rollout',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><FeatureFlagRolloutPlanner /></LazyPage></ContextGuard>,
      },

      // QA & Testing tools
      {
        path: 'admin/coverage-map',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><CoverageMap /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/test-scenarios',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><TestScenarioManager /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/mock-data',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><MockDataGenerator /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/chaos-mode',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ChaosMode /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/visual-regression',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><VisualRegressionDashboard /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/bug-reports',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><BugReportWidget /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/accessibility',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><AccessibilityAuditPanel /></LazyPage></ContextGuard>,
      },

      // Design & UX tools
      {
        path: 'admin/token-changelog',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><DesignTokenChangelog /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/responsive-preview',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ResponsivePreview /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/flow-recorder',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><UserFlowRecorder /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/annotations',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><AnnotationMode /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/tour-builder',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><OnboardingTourBuilder /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/feature-requests',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><FeatureRequestBoard /></LazyPage></ContextGuard>,
      },

      // Legal & Compliance tools
      {
        path: 'admin/consent',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><ConsentDashboard /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/terms-versions',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><TermsVersionManager /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/pii-scanner',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><PIIScanner /></LazyPage></ContextGuard>,
      },

      // Support tools
      {
        path: 'admin/support-actions',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><SupportQuickActions /></LazyPage></ContextGuard>,
      },
      {
        path: 'admin/support-history',
        element: <ContextGuard context="platform" roles={['admin']}><LazyPage><SupportHistory /></LazyPage></ContextGuard>,
      },
    ],
  },

  // ============================================================================
  // Catch-all redirect to home
  // ============================================================================
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])

